/**
 * Product quantities for a spray: unit conversion and what goes in one tank.
 *
 * Mirrors `convertAmount` / `expectedForTank` in HHNodeServer's
 * sprayTankService, which stays authoritative for the tank log. This copy is
 * for the screens that show the dose before any tank exists.
 */

/** Size of each product unit in its family's base unit (mL or g). */
const UNIT_SCALE: Record<string, { family: 'volume' | 'mass'; toBase: number }> = {
  l:  { family: 'volume', toBase: 1000 },
  ml: { family: 'volume', toBase: 1 },
  kg: { family: 'mass',   toBase: 1000 },
  g:  { family: 'mass',   toBase: 1 },
};

function scaleOf(unit: string | null | undefined) {
  return UNIT_SCALE[String(unit ?? '').trim().toLowerCase()] ?? null;
}

/**
 * Convert an amount between units of one family (L↔mL, kg↔g). Null across
 * families: litres cannot become kilograms without a density nobody recorded.
 */
export function convertAmount(qty: number, from: string, to: string): number | null {
  const a = scaleOf(from);
  const b = scaleOf(to);
  if (!a || !b || a.family !== b.family || !Number.isFinite(qty)) return null;
  return (qty * a.toBase) / b.toBase;
}

/** 'mL/100L' → ['mL', '100L']. */
function splitRateUnit(rateUnit: string): [string, string] {
  const i = rateUnit.indexOf('/');
  return i < 0 ? [rateUnit, ''] : [rateUnit.slice(0, i), rateUnit.slice(i + 1)];
}

/**
 * Re-express a rate in another unit, keeping the dose the same:
 * 1.5 L/ha → 1500 mL/ha. Only the product half may change; switching per
 * hectare to per 100 L is a different dose, not a different unit, and
 * returns null.
 */
export function convertRate(rate: number, fromUnit: string, toUnit: string): number | null {
  const [fromQty, fromPer] = splitRateUnit(fromUnit);
  const [toQty, toPer] = splitRateUnit(toUnit);
  if (fromPer.toLowerCase() !== toPer.toLowerCase()) return null;
  const converted = convertAmount(rate, fromQty, toQty);
  // Strip float noise (0.1 L → 100.00000000000001 mL).
  return converted == null ? null : parseFloat(converted.toPrecision(12));
}

function isDilute(rateUnit: string): boolean {
  return /\/\s*100\s*L$/i.test(rateUnit);
}

/**
 * What one tank of `waterL` litres should receive of a product.
 *
 * Dilute (per 100 L) rates dose the water. Per-hectare rates dose the ground,
 * so the water is turned into an area through the sprayer's output rate —
 * without one the amount is unknowable, and null says so rather than guessing.
 */
export function amountForTank(opts: {
  rate: number | null | undefined;
  rateUnit: string;
  concentrationFactor?: number;
  waterL: number | null | undefined;
  outputRateLpha?: number | null;
}): { amount: number; unit: string } | null {
  const { rate, rateUnit, concentrationFactor = 1, waterL, outputRateLpha } = opts;
  const unit = splitRateUnit(rateUnit || 'L/ha')[0] || 'L';
  if (!rate || rate <= 0 || !waterL || waterL <= 0) return null;
  if (isDilute(rateUnit)) {
    return { amount: (waterL / 100) * rate * concentrationFactor, unit };
  }
  if (!outputRateLpha || outputRateLpha <= 0) return null;
  return { amount: (waterL / outputRateLpha) * rate * concentrationFactor, unit };
}

/**
 * What the whole job takes of a product.
 *
 * Per-hectare rates dose the ground, so the area alone decides it — no output
 * rate needed. Dilute (per 100 L) rates dose the water, so the job's water
 * decides it. Null when the one it needs is missing.
 */
export function amountForJob(opts: {
  rate: number | null | undefined;
  rateUnit: string;
  concentrationFactor?: number;
  areaHa: number | null | undefined;
  waterL: number | null | undefined;
}): { amount: number; unit: string } | null {
  const { rate, rateUnit, concentrationFactor = 1, areaHa, waterL } = opts;
  const unit = splitRateUnit(rateUnit || 'L/ha')[0] || 'L';
  if (!rate || rate <= 0) return null;
  if (isDilute(rateUnit)) {
    if (!waterL || waterL <= 0) return null;
    return { amount: (waterL / 100) * rate * concentrationFactor, unit };
  }
  if (!areaHa || areaHa <= 0) return null;
  return { amount: areaHa * rate * concentrationFactor, unit };
}

/**
 * An amount written the way somebody measures it out: under a litre (or
 * kilogram) in mL (or g), a thousand mL or more in L. Enough digits that a
 * small dose is not rounded away — 0.04 L reads "40 mL", not "0.0 L".
 */
export function formatAmount(amount: number, unit: string): string {
  let qty = amount;
  let u = unit;
  const s = scaleOf(unit);
  if (s) {
    const base = amount * s.toBase;
    const small = s.family === 'volume' ? 'mL' : 'g';
    const large = s.family === 'volume' ? 'L' : 'kg';
    if (base < 1000) { qty = base; u = small; } else { qty = base / 1000; u = large; }
  }
  const digits = qty >= 100 ? 0 : qty >= 10 ? 1 : 2;
  return `${parseFloat(qty.toFixed(digits))} ${u}`;
}
